"use client";

import { useAuthStore } from "@kaplan-labs/up-auth-api-client";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { FormProvider, useForm } from "react-hook-form";

import { analyticsEvent } from "@up/analytics";
import {
  ProfileType,
  catalogueItemsStore,
  logError,
  useGeoData,
  useProfile,
  useTheme,
} from "@up/data";
import {
  Animation,
  BoundaryFallback,
  BrowserOnly,
  LoadingModal,
  Progress,
  Toast,
  ToastTitle,
} from "@up/ui";

import { Bottom } from "./bottom";
import { EnforceAuthentication } from "./enforce-authentication";
import { EnforcePageParam } from "./enforce-page-param";
import { Guardian } from "./guardian";
import { formatForServer, logBoundaryError, setupPhoneNumber } from "./helpers";
import { network } from "./network";
import { Thanks } from "./thanks";
import { UserDataLoadError } from "./user-data-load-error";
const baseURL = process.env.NEXT_PUBLIC_HALL_OF_RECORDS_URI;

/**
 * Go
 * @param {object} props
 * @param {array} props.pages
 * @param {React.ComponentType<React.ComponentProps<typeof CompleteErrorMessage>>} [props.CompleteErrorMessageOverride]
 */
function Go({ CompleteErrorMessageOverride, pages }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const theme = useTheme();
  const auth = useAuthStore();
  const profile = useProfile();
  const geo = useGeoData();

  network.setPartner({
    _id: theme.partnerID,
    name: theme.partnerName,
  });

  const FORM_SUBMISSION_URL = baseURL + `/v2/apps/${theme.partnerID}`;
  // Had a strange issue where `navigateToNextPage` did not have access to
  // the pages variable. Maybe some kind of edge case with scoping or
  // accessing variables when invoked from a callback?
  const Pages = pages;

  const [userData, _setUserData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [initialFetchDidError, setInitialFetchDidError] = useState(false);
  const [initialFetchIsLoading, setInitialFetchIsLoading] = useState(false);
  const [didAttemptComplete, setDidAttemptComplete] = useState(false);
  const [completeErrors, setCompleteErrors] = useState(null);

  const setUserData = (_data) => {
    const data = structuredClone(_data);
    setupPhoneNumber("student-phone-number", data);
    setupPhoneNumber("guardian-phone-number", data);
    _setUserData(data);
  };

  const formRef = useRef(null);

  const useFormMethods = useForm({
    defaultValues: userData,
  });

  const {
    formState: { errors },
    handleSubmit,
    reset,
    setError,
  } = useFormMethods;

  // Force react-hook-form to start fresh when navigating to a new page
  useEffect(
    function () {
      reset(userData);
    },
    [reset, userData],
  );

  const CURRENT_PAGE = parseInt(searchParams.get("page")) || 1;
  const TOTAL_PAGES = pages.length;
  const CurrentPageComponent = useMemo(() => {
    return pages[CURRENT_PAGE - 1 || 0];
  }, [CURRENT_PAGE, pages]);

  useEffect(
    function checkForReturningStudent() {
      const getExistingData = async () => {
        setInitialFetchIsLoading(true);

        try {
          const json = await network.request({ url: FORM_SUBMISSION_URL });

          if (json) {
            setUserData(json);
          }
        } catch (error) {
          // if we get a 404 that means there isn't existing data
          if (error.response?.status !== 404) {
            setInitialFetchDidError(true);
            logError({
              action: "APPLICATION_INITIAL_FETCH",
              error,
              context: { up_userId: auth.currentUser?.id },
            });
          }
        }

        setInitialFetchIsLoading(false);
      };

      if (auth.currentUser && !Object.keys(userData).length) {
        getExistingData();
      } else {
        setInitialFetchIsLoading(false);
      }
    },
    [auth, FORM_SUBMISSION_URL, userData],
  );

  const navigateToPage = function (page) {
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", String(page));
    window.history.pushState(null, "", `${pathname}?${params.toString()}`);
    reset(userData);
  };

  const navigateToPreviousPage = function () {
    const PreviousPageComponent = Pages[CURRENT_PAGE - 2];

    if (PreviousPageComponent.beforeRender) {
      PreviousPageComponent.beforeRender({
        router,
        navigateToPage,
        userData,
        currentPage: CURRENT_PAGE,
        totalPages: TOTAL_PAGES,
        direction: -1,
      });
    } else {
      navigateToPage(CURRENT_PAGE - 1);
    }
  };

  const navigateToNextPage = function () {
    const NextPageComponent = Pages[CURRENT_PAGE];
    if (NextPageComponent && NextPageComponent.beforeRender) {
      NextPageComponent.beforeRender({
        router,
        navigateToPage,
        userData,
        currentPage: CURRENT_PAGE,
        totalPages: TOTAL_PAGES,
        direction: 1,
      });
    } else if (CURRENT_PAGE !== TOTAL_PAGES) {
      navigateToPage(CURRENT_PAGE + 1);
    }
  };

  const persistUserData = async function (data) {
    setIsSaving(true);
    setSaveError(false);

    try {
      const response = await network.request({
        url: FORM_SUBMISSION_URL,
        data,
        options: {
          method: data._id ? "PATCH" : "POST",
        },
      });
      setUserData(response);
      setIsSaving(false);
      return [response, null];
    } catch (error) {
      setIsSaving(false);
      setSaveError(true);
      logError({
        action: "APPLICATION_SAVE_DID_ERROR",
        error,
        context: { up_email: auth.currentUser.email },
      });
      return [null, error];
    }
  };

  /**
   * onSubmit
   * @param {object} data - data from react-hook-form
   */
  const onSubmit = async function (data) {
    const PHONE_KEY_STUDENT = "student-phone-number";
    const PHONE_KEY_GUARDIAN = "guardian-phone-number";
    const COUNTRY_CODE_KEY_STUDENT = `${PHONE_KEY_STUDENT}-country-code`;
    const COUNTRY_CODE_KEY_GUARDIAN = `${PHONE_KEY_GUARDIAN}-country-code`;

    /*
      Accessing values from the <form> because `data`
      has all data across all pages. We only want to validate phone and email
      if those fields are on the current page
    */
    let formData;
    if (formRef.current) {
      formData = Object.fromEntries(new FormData(formRef.current));
    } else {
      setTimeout(() => {
        onSubmit(data);
      }, 200);
      return;
    }

    const payload = {
      ...userData,
      ...data,
      "student-email": auth.currentUser.email,
      currentPage: CURRENT_PAGE,
    };

    const validateAndFormatPhone = async (phoneKey, countryCodeKey) => {
      setIsSaving(true);
      const phoneValue = formData?.[phoneKey];
      const countryCodeValue = formData?.[countryCodeKey];

      try {
        const response = await network.request({
          url:
            baseURL +
            `/v2/telephony/validate?phone=${phoneValue}&countryCode=${countryCodeValue}`,
        });

        return [response?.phone ?? phoneValue, null];
      } catch (err) {
        if (err.response?.status === 406) {
          setError(phoneKey, {
            type: "phone",
            message: "Please enter a valid phone number",
          });
        } else {
          logError({
            action: "APPLICATION_PHONE_VALIDATION",
            error: err,
            context: { up_userId: auth.currentUser?.id },
          });
          setSaveError(true);
        }

        setIsSaving(false);
        return [null, err];
      }
    };

    if (formData?.[PHONE_KEY_STUDENT] && formData?.[COUNTRY_CODE_KEY_STUDENT]) {
      const [formattedPhone, error] = await validateAndFormatPhone(
        PHONE_KEY_STUDENT,
        COUNTRY_CODE_KEY_STUDENT,
      );
      if (error) {
        return;
      }
      if (formattedPhone) {
        payload[PHONE_KEY_STUDENT] = formattedPhone;
      }
    }

    if (
      formData?.[PHONE_KEY_GUARDIAN] &&
      formData?.[COUNTRY_CODE_KEY_GUARDIAN]
    ) {
      const [formattedPhone, error] = await validateAndFormatPhone(
        PHONE_KEY_GUARDIAN,
        COUNTRY_CODE_KEY_GUARDIAN,
      );
      if (error) {
        return;
      }
      if (formattedPhone) {
        payload[PHONE_KEY_GUARDIAN] = formattedPhone;
      }
    }

    if (formData?.["guardian-email"]) {
      setIsSaving(true);
      const EMAIL_KEY = "guardian-email";
      const EMAIL = formData[EMAIL_KEY];

      const EMAIL_VALIDATION_URL = baseURL + "/v2/email/validate";

      try {
        await network.request({
          url: EMAIL_VALIDATION_URL,
          data: {
            email: EMAIL,
          },
          options: {
            method: "POST",
          },
        });
      } catch (err) {
        if (err.response?.status === 406) {
          setError(EMAIL_KEY, {
            type: "email",
            message: "Please enter a valid email address",
          });
        } else {
          logError({
            action: "APPLICATION_GUARDIAN_EMAIL_VALIDATION",
            error: err,
            context: { up_userId: auth.currentUser?.id },
          });
          setSaveError(true);
        }
        setIsSaving(false);
        return;
      }
    }

    if (CURRENT_PAGE === TOTAL_PAGES || didAttemptComplete) {
      setIsSaving(true);
      setCompleteErrors(null);
      setDidAttemptComplete(true);
      formatForServer(payload);

      try {
        const response = await network.request({
          url: baseURL + "/v2/apps/complete",
          data: payload,
          options: {
            method: "POST",
          },
        });

        if (response.completedAt) {
          try {
            const catalogueMap = catalogueItemsStore.get();
            const selectedIds = Array.isArray(payload["program-selection"])
              ? payload["program-selection"]
              : [];
            const selectedItems = selectedIds
              .map((id) => catalogueMap[id])
              .filter(Boolean);
            const selectedCourseNames = selectedItems.map((item) => item.name);

            const courseType = [];
            if (selectedItems.some((item) => item.type === "program")) {
              courseType.push("enrichment");
            }
            if (selectedItems.some((item) => item.type === "collegeCredit")) {
              courseType.push("credit");
            }

            analyticsEvent({
              event: "applicationSubmit",
              courseIds: selectedIds,
              courses: selectedCourseNames,
              course_type: courseType,
            });
          } catch (err) {
            analyticsEvent({
              event: "applicationSubmit",
            });
          }
        }

        setUserData(response);
      } catch (err) {
        logError({ action: "APPLICATION_COMPLETE", error: err });
        const response = err?.response ? err?.data : undefined;
        setCompleteErrors(response?.errors);
        await persistUserData(payload);
      }

      if (CURRENT_PAGE !== TOTAL_PAGES && !userData.completedAt) {
        navigateToNextPage();

        window.scrollTo({
          top: 100,
          left: 100,
          behavior: "smooth",
        });
      }

      setIsSaving(false);
    } else {
      const [saveResponse] = await persistUserData(formatForServer(payload));
      if (saveResponse) {
        navigateToNextPage();

        window.scrollTo({
          top: 100,
          left: 100,
          behavior: "smooth",
        });
      }
    }
  };

  const SHOW_COMPLETE = userData.completedAt;

  const IS_GUARDIAN = profile.data?.type === ProfileType.Guardian;

  const SHOW_FORM =
    !geo.isLoading &&
    !initialFetchIsLoading &&
    !SHOW_COMPLETE &&
    !initialFetchDidError &&
    !IS_GUARDIAN;

  return (
    <div className="up-form-container pt-6">
      <div className="mb-12">
        <div className="mb-3">
          <h1 className={theme.heading1Class}>Application</h1>
        </div>
        {CURRENT_PAGE > 1 && !SHOW_COMPLETE ? (
          <div style={{ color: theme.primaryColor }}>
            <Progress
              isSaving={isSaving}
              label={`Page ${CURRENT_PAGE} of ${TOTAL_PAGES}`}
              value={
                CURRENT_PAGE === 1
                  ? 0
                  : Math.min((CURRENT_PAGE / TOTAL_PAGES) * 100, 95)
              }
            />
          </div>
        ) : null}
      </div>

      {initialFetchDidError || geo.didError ? <UserDataLoadError /> : null}

      {initialFetchIsLoading || geo.isLoading ? <LoadingModal /> : null}

      {SHOW_COMPLETE ? <Thanks status={userData.status} /> : null}
      {IS_GUARDIAN ? <Guardian /> : null}
      {SHOW_FORM ? (
        <Animation type="splash-in">
          {saveError ? (
            <div className="mb-6">
              <Toast variant="error">
                <ToastTitle inline variant="error">
                  We could not save your progress.
                </ToastTitle>
                Please try again or contact support at{" "}
                <a className="underline" href={`mailto:${theme.supportEmail}`}>
                  {theme.supportEmail}
                </a>
              </Toast>
            </div>
          ) : null}

          <FormProvider {...useFormMethods}>
            <form
              className="mb-8"
              disabled={isSaving || saveError}
              onSubmit={handleSubmit(onSubmit)}
              noValidate={true}
              ref={formRef}
            >
              <CurrentPageComponent
                geo={geo.data}
                libraryTheme={theme}
                bottom={(disableButtons = false) => {
                  return (
                    <Bottom
                      completeErrors={completeErrors}
                      CompleteErrorMessageOverride={
                        CompleteErrorMessageOverride
                      }
                      currentPage={CURRENT_PAGE}
                      disableButtons={disableButtons || isSaving}
                      errors={errors}
                      isSaving={isSaving}
                      navigateToPage={navigateToPage}
                      navigateToPreviousPage={navigateToPreviousPage}
                      onLastPage={CURRENT_PAGE === TOTAL_PAGES}
                      saveError={saveError}
                    />
                  );
                }}
                userData={userData}
              />
            </form>
          </FormProvider>
        </Animation>
      ) : null}
    </div>
  );
}

/**
 * ApplicationFormController
 * @param {object} props
 * @param {array} props.pages
 * @param {React.ComponentType<React.ComponentProps<typeof CompleteErrorMessage>>} [props.CompleteErrorMessageOverride]
 */
export function ApplicationFormController({
  CompleteErrorMessageOverride,
  pages,
}) {
  return (
    <Suspense>
      <BrowserOnly>
        <ErrorBoundary
          FallbackComponent={BoundaryFallback}
          onError={logBoundaryError}
        >
          <EnforceAuthentication>
            <EnforcePageParam stepCount={pages.length}>
              <Go
                CompleteErrorMessageOverride={CompleteErrorMessageOverride}
                pages={pages}
              />
            </EnforcePageParam>
          </EnforceAuthentication>
        </ErrorBoundary>
      </BrowserOnly>
    </Suspense>
  );
}
